// Extension distribution: typed endpoints + the installed-vs-latest hook used by the top bar,
// the sidebar and /extension. Contract: @gmx/shared/features/extension.

import {
  extensionUpdateStatus,
  type AdminExtensionRelease,
  type AdminExtensionReleasesRes,
  type ExtensionLatest,
  type ExtensionReleasesRes,
  type ExtensionUpdateStatus,
  type UpdateExtensionReleaseReq,
} from "@gmx/shared/features/extension";
import { useQuery } from "@tanstack/react-query";
import { api, isApiError } from "../../lib/api";
import { useExtensionStatus } from "../../lib/sso";

export const extensionApi = {
  /** null when nothing is published yet (404 no_release). */
  latest: async (): Promise<ExtensionLatest | null> => {
    try {
      return await api<ExtensionLatest>("/api/extension/latest");
    } catch (err) {
      if (isApiError(err, 404)) return null;
      throw err;
    }
  },
  releases: () => api<ExtensionReleasesRes>("/api/extension/releases"),
  downloadUrl: (version?: string) => (version ? `/api/extension/download?version=${encodeURIComponent(version)}` : "/api/extension/download"),
  admin: {
    list: () => api<AdminExtensionReleasesRes>("/api/extension/admin/releases"),
    upload: (form: FormData) => api<AdminExtensionRelease>("/api/extension/admin/releases", { method: "POST", body: form }),
    update: (version: string, body: UpdateExtensionReleaseReq) =>
      api<AdminExtensionRelease>(`/api/extension/admin/releases/${encodeURIComponent(version)}`, { method: "PATCH", body }),
    remove: (version: string) => api<{ ok: boolean }>(`/api/extension/admin/releases/${encodeURIComponent(version)}`, { method: "DELETE" }),
  },
};

export const extensionKeys = {
  latest: ["extension", "latest"] as const,
  releases: ["extension", "releases"] as const,
  admin: ["admin", "extension-releases"] as const,
};

/** Latest release, refreshed while the app is open and whenever focus returns. */
export function useLatestExtension() {
  return useQuery({
    queryKey: extensionKeys.latest,
    queryFn: extensionApi.latest,
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: "always",
    retry: 1,
  });
}

export type PortalExtensionStatus = ExtensionUpdateStatus | "checking";

export interface ExtensionUpdateInfo {
  status: PortalExtensionStatus;
  installed: string | null;
  latest: ExtensionLatest | null;
  /** Latest release has loaded (or failed). */
  ready: boolean;
}

/**
 * Installed (from the SSO bridge's sso-pong) vs latest (API) → one status for every surface.
 * "checking" while we're still looking for the extension.
 */
export function useExtensionUpdate(): ExtensionUpdateInfo {
  const ext = useExtensionStatus();
  const latest = useLatestExtension();
  const data = latest.isError ? null : latest.data ?? null;
  const installed = ext.detected ? ext.version : null;
  let status: PortalExtensionStatus;
  if (ext.status === "checking") status = "checking";
  else status = extensionUpdateStatus(installed ?? (ext.detected ? "?" : null), data?.version, data?.minSupportedVersion);
  return { status, installed, latest: data, ready: !latest.isPending };
}

export function formatBytes(n: number | null | undefined): string {
  if (!n || n < 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
